import React, { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { FaRegCopy, FaCheck } from "react-icons/fa";
import useTrivvyShortLink, { SHORT_LINK_STATUS } from "../../../../hooks/useTrivvyShortLink";
import ShortLinkStatus from "./ShortLinkStatus";
import { toastContainer } from "../../../../utils/toast";
import { copyTextToClipboard } from "../../../../utils/trivvyShortLink";
import {
  SHARE_LINK_LABEL,
  SHARE_LINK_LOADING,
  SHARE_LINK_COPY,
  SHARE_LINK_COPIED_LABEL,
  SHARE_LINK_COPIED,
  SHARE_LINK_COPY_FAILED,
} from "./shareCopy";

// How long the Copy button shows its "Copied" confirmation.
const COPIED_FEEDBACK_MS = 2000;

// TBV-938: the "Shareable Link" row used by the Share modal and the list
// Copy Link dialog. Only this row loads; everything around it stays usable.
// - Copy is disabled until a validated URL is ready, and "Link copied!" is
//   shown only after the clipboard write resolves.
// - The original long link is offered only for server/offline failures, and
//   only when the user explicitly chooses it.
const ShareableLinkField = ({
  itemType,
  itemId,
  source,
  fallbackUrl,
  knownShortUrl,
  onResolved,
}) => {
  const shortLink = useTrivvyShortLink({ itemType, itemId, source, knownShortUrl, onResolved });
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [copyPending, setCopyPending] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const isMountedRef = useRef(true);
  const copiedTimerRef = useRef(null);
  const linkInputRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(copiedTimerRef.current);
    };
  }, []);

  // A new item / retry starts clean: no fallback choice, no "Copied" state.
  useEffect(() => {
    setIsUsingFallback(false);
    setIsCopied(false);
  }, [itemId, source, shortLink.linkStatus]);

  let copyableUrl = shortLink.shortUrl;
  if (!copyableUrl && shortLink.canOfferFallback && isUsingFallback && fallbackUrl) {
    copyableUrl = fallbackUrl;
  }

  // The clipboard write starts synchronously inside the click (Safari/mobile).
  const handleCopy = () => {
    if (!copyableUrl || copyPending) {
      return;
    }
    setCopyPending(true);
    copyTextToClipboard(copyableUrl)
      .then(() => {
        toastContainer(SHARE_LINK_COPIED, "success");
        if (isMountedRef.current) {
          setIsCopied(true);
          clearTimeout(copiedTimerRef.current);
          copiedTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              setIsCopied(false);
            }
          }, COPIED_FEEDBACK_MS);
        }
      })
      .catch(() => {
        toastContainer(SHARE_LINK_COPY_FAILED, "error");
        if (linkInputRef.current) {
          linkInputRef.current.focus();
          linkInputRef.current.select();
        }
      })
      .then(() => {
        if (isMountedRef.current) {
          setCopyPending(false);
        }
      });
  };

  return (
    <div>
      <span className="share_label_caps">{SHARE_LINK_LABEL}</span>
      <div className="share_link_row">
        <input
          ref={linkInputRef}
          type="text"
          className="form-control share_link_field"
          value={copyableUrl}
          placeholder={shortLink.linkStatus === SHORT_LINK_STATUS.LOADING ? SHARE_LINK_LOADING : ""}
          aria-busy={shortLink.linkStatus === SHORT_LINK_STATUS.LOADING}
          aria-label={SHARE_LINK_LABEL}
          readOnly
        />
        <Button
          className={`share_page_btn_black share_link_copy_btn ${
            isCopied ? "share_link_copy_btn_copied" : ""
          }`}
          disabled={!copyableUrl || copyPending}
          onClick={handleCopy}
        >
          {isCopied ? (
            <>
              <FaCheck className="share_link_copy_icon" /> {SHARE_LINK_COPIED_LABEL}
            </>
          ) : (
            <>
              <FaRegCopy /> {SHARE_LINK_COPY}
            </>
          )}
        </Button>
      </div>
      <ShortLinkStatus
        className="mt-2"
        shortLink={shortLink}
        hasFallback={Boolean(fallbackUrl)}
        isUsingFallback={isUsingFallback}
        onUseFallback={() => setIsUsingFallback(true)}
      />
    </div>
  );
};

export default ShareableLinkField;
